import type { FC } from 'react'
import { useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, IconButton, Tooltip } from '@mui/material'
import { ViewSidebarOutlined } from '@mui/icons-material'
import { COLLAPSED_WIDTH } from '../constants'
import { standardTooltipProps } from '@/common/components/tooltipStyles'

interface SectionProps {
  readonly isCollapsed?: boolean
}

type SectionComponent = FC<SectionProps>

interface CollapsedRailProps {
  readonly onExpand: () => void
  readonly StatusSection: SectionComponent
  readonly NavigationSection: SectionComponent
  readonly MenuSection: SectionComponent
  readonly UserSection: SectionComponent
}

export const CollapsedRail: FC<CollapsedRailProps> = ({
  onExpand,
  StatusSection,
  NavigationSection,
  MenuSection,
  UserSection,
}) => {
  const handleExpandClick = useCallback(() => {
    onExpand()
  }, [onExpand])

  return (
    <Box sx={railContainerSx}>
      <Box sx={topSectionSx}>
        <Tooltip {...standardTooltipProps} title="Expand sidebar" placement="right">
          <IconButton
            onClick={handleExpandClick}
            size="small"
            sx={menuButtonSx}
            aria-label="Expand sidebar"
          >
            <ViewSidebarOutlined sx={menuIconSx} />
          </IconButton>
        </Tooltip>

        <Box sx={statusContainerSx}>
          <StatusSection isCollapsed />
        </Box>

        <Box sx={navigationContainerSx}>
          <NavigationSection isCollapsed />
        </Box>
      </Box>

      <Box sx={spacerSx} />

      <Box sx={menuContainerSx}>
        <MenuSection isCollapsed />
      </Box>

      <Box sx={userContainerSx}>
        <UserSection isCollapsed />
      </Box>
    </Box>
  )
}

const railContainerSx: SxProps<Theme> = {
  width: COLLAPSED_WIDTH,
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'transparent',
}

const topSectionSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  pt: 1.5,
  pl: 1.5,
  gap: 1,
}

const menuButtonSx: SxProps<Theme> = {
  color: 'text.disabled',
  opacity: 0.6,
  '&:hover': {
    color: 'text.primary',
    opacity: 1,
    backgroundColor: 'action.hover',
  },
}

const menuIconSx: SxProps<Theme> = {
  fontSize: 20,
}

const statusContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  mt: 2,
}

const navigationContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  mt: 2,
}

const spacerSx: SxProps<Theme> = {
  flex: 1,
}

const menuContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  pl: 1.5,
  pb: 2,
}

const userContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  pl: 1.5,
  pb: 2.5,
}
